import { type FormEvent, useCallback, useMemo, useState } from 'react';
import { Box, Button, FormControl, FormLabel, Input, Flex, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import type { JSONContent } from '@tiptap/core';
import { useSnapshot } from 'valtio';

import { DukeTiptapEditor } from '@duke-hq/ui/components/DukeTiptapEditor/DukeTiptapEditor';
import { JSONContentSchema } from '@duke-hq/shared-constants/tiptap';
import { useDukeFileUpload } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/blocknode/useDukeFileUpload';
import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { trpcAdmin } from '../../../lib/trpcAdmin';

import { unitAssessmentState } from '../../UnitAssessmentState';

const DEFAULT_CONTENT: JSONContent = { type: 'doc', content: [] };

type FormSectionEditorProps = {
  refetch: () => void;
};

export function FormSectionEditor({ refetch }: FormSectionEditorProps) {
  const { currentSlide, editorMode, currentAssessmentId, currentFormSectionId } =
    useSnapshot(unitAssessmentState);

  const { uploadFile } = useDukeFileUpload({
    fileAttributes: {
      attachedToKind: 'UNIT_ASSESSMENT',
      attachedToId: currentAssessmentId,
    },
  });

  const currentFormSection = useMemo(() => {
    if (editorMode === EditorMode.EDIT && currentSlide !== null && 'sections' in currentSlide) {
      const sections = currentSlide.sections ?? [];
      return sections.find(({ id }) => id === currentFormSectionId);
    }
  }, [currentSlide, editorMode, currentFormSectionId]);

  const [title, setTitle] = useState(currentFormSection?.title ?? '');
  const [descriptionEditorContent, setDescriptionEditorContent] = useState<JSONContent>();

  const initialDescriptionContent = useMemo(() => {
    if (currentFormSection && 'description' in currentFormSection) {
      const parsedDescription = JSONContentSchema.safeParse(currentFormSection.description);
      if (parsedDescription.success) {
        return parsedDescription.data;
      }

      return DEFAULT_CONTENT;
    }
    return DEFAULT_CONTENT;
  }, [currentFormSection]);

  const createFormSectionMutation = trpcAdmin.unitAssessment.createFormSection.useMutation({
    onSuccess: (data) => {
      unitAssessmentState.setNewlyCreatedFormSectionId(data.id);
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      refetch();
    },
  });

  const updateFormSectionMutation = trpcAdmin.unitAssessment.updateFormSection.useMutation({
    onSuccess: () => {
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      refetch();
    },
  });

  const handleSave = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const payload = {
        title,
        description: descriptionEditorContent ?? initialDescriptionContent,
      };

      if (editorMode === EditorMode.CREATE && currentSlide) {
        await createFormSectionMutation.mutateAsync({ ...payload, formSlideId: currentSlide.id });
      } else if (editorMode === EditorMode.EDIT && currentFormSectionId !== null) {
        await updateFormSectionMutation.mutateAsync({ id: currentFormSectionId, ...payload });
      }
    },
    [
      title,
      descriptionEditorContent,
      initialDescriptionContent,
      createFormSectionMutation,
      updateFormSectionMutation,
      currentSlide,
      editorMode,
      currentFormSectionId,
    ],
  );

  return (
    <form onSubmit={handleSave}>
      <VStack spacing={4} align="stretch">
        <FormControl>
          <FormLabel htmlFor="form-title">
            <Trans>Title</Trans>
          </FormLabel>
          <Input
            id="form-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t`Optional`}
          />
        </FormControl>

        <FormControl>
          <FormLabel htmlFor="form-description">
            <Trans>Description</Trans>
          </FormLabel>
          <Box
            w="100%"
            borderWidth="1px"
            borderRadius="md"
            outline="1px"
            boxShadow="sm"
            minH="200px"
          >
            <DukeTiptapEditor
              initialContent={initialDescriptionContent}
              onChange={(content) => setDescriptionEditorContent(content)}
              uploadFn={uploadFile}
            />
          </Box>
        </FormControl>

        <Flex justify="flex-end">
          <Button right={0} type="submit">
            {editorMode === EditorMode.CREATE ? <Trans>Add</Trans> : <Trans>Update</Trans>}
          </Button>
        </Flex>
      </VStack>
    </form>
  );
}
